import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { MAX_QUERY_LIMIT } from 'src/graphql/constants';
import { QueryVariables, Records } from 'src/graphql/types';
import type { MembershipRaw } from 'src/types';

const GET_MEMBERSHIPS_QUERY = gql`
  query GetMembership($params: QueryParams) @entity(name: "Membership") {
    getMembership(queryParams: $params) {
      records {
        id
        name
      }
    }
  }
`;

interface GetMembershipsQuery {
  getMembership: Records<MembershipRaw>;
}

export interface Membership {
  id: string;
  name: string;
}

export const useMemberships = (options?: QueryHookOptions<GetMembershipsQuery>) => {
  const { data, ...rest } = useQuery<GetMembershipsQuery, QueryVariables>(
    GET_MEMBERSHIPS_QUERY,
    {
      ...options,
      variables: {
        params: {
          ...options?.variables,
          sorting: { attribute: 'name' },
          limit: MAX_QUERY_LIMIT,
        },
      },
    },
  );

  const memberships = useMemo(() => {
    if (!data?.getMembership?.records?.length) {
      return null;
    }

    return data?.getMembership.records.map((membership: MembershipRaw) => {
      return {
        id: membership.id,
        name: membership.name,
      };
    });
  }, [data?.getMembership]);

  return {
    memberships,
    ...rest,
  };
};
export function useMembershipOptions(options?: QueryHookOptions<GetMembershipsQuery>) {
  const { memberships } = useMemberships(options);
  return useMemo(() => {
    if (!memberships) return [];
    return memberships.map((item) => ({
      id: item.id,
      value: item.id,
      label: item.name,
    }));
  }, [memberships]);
}
